import BreadCrumb from "components/common/BreadCrumb";
import PageTitleSection from "components/common/PageTitleSection";
import React, { useEffect, useState } from "react";
import CustomEditor from "../CommonChatComponent/Editor/Editor";
import moment from "moment";
import TabsContainer from "components/common/TabsContainer";
import { chatActions, flowActions, workforceActions } from "redux/Ldb/actions";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import useGetUserFlowMode from "hooks/Flow/useGetUserFlowMode";
import ChatHistory from "../CommonChatComponent/ChatHistory";
import { useCoAgent, useCopilotChatHeadless_c } from "@copilotkit/react-core";
import useGetAllAiConversations from "hooks/Chat/useGetAllAiConversations";
import { v4 as uuidv4 } from "uuid";
import Loader from "components/common/Loader";
import toast from "react-hot-toast";
import CustomToast from "components/common/CustomToast";
import { isFileSizeLessThanOrEqualTo } from "helpers/compareFileSizeInMb";
import { firebaseService } from "services/firebaseService";
import { AgentState } from "types/salesTargetType";
import { truncate } from "lodash";
import { displayFileIcon, getFromStorage } from "helpers";
import { useNavigate } from "react-router-dom";
import { SPACES } from "constants/urls";
import useGetAllAgent from "hooks/workforceHooks/useGetAllAgent";
import { convertBytes } from "helpers/displayFileSize";
import { Tooltip } from "react-tooltip";
import { spaceActions } from "redux/Ldb/actions/spaceActions";
import FlowInsightModal from "./FlowInsightModal";
import FlowAllTask from "./FlowAllTask";
import FlowAllAgent from "./FlowAllAgent";

const Flow = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const profile = getFromStorage("ally-user");
    const [mode, setMode] = useState<string>("");
    const [showWorkforce, setShowWorkforce] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<number>(0);
    const tabs = ["All Agents", "All Tasks"];
    const [message, setMessage] = useState<string>("");
    const { mode: flowMode } = useGetUserFlowMode();
    const [previousChatLayout, setPreviousChatLayout] = useState<boolean>(true);
    const [threadId, setThreadId] = useState<string>("");
    const [contentMessage, setContentMessage] = useState<string>("");
    const [selectedIds, setSelectedIds] = useState<{ [key: string]: any }[]>([]);
    const [currentSelectedIds, setCurrentSelectedIds] = useState<{ [key: string]: any }[]>([]);
    const [insights, setInsights] = useState<{ [key: string]: any }>({});
    const [insightsModal, setInsightsModal] = useState<boolean>(false);
    const [summary, setSummary] = useState<string>("");
    const [filesAttached, setFilesAttached] = useState<{ [key: string]: any }[]>([]);
    const [creatingNewThread, setCreatingNewThread] = useState<boolean>(false);
    const [uploadingDoc, setUploadingDoc] = useState<boolean>(false);
    const [loadingAttachName, setLoadingAttachName] = useState<string>("");
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [filteredList, setFilteredList] = useState<any[]>([]);
    const [refetch, setRefetch] = useState<boolean>(false);
    const {
        data: allAgentList,
        isFetching: fetchingAllAgents,
        setDebouncedSearch,
        setType,
        type,
    } = useGetAllAgent({
        refetch,
    });

    const [isActive, setIsActive] = useState<{
        id?: string;
        isActive?: boolean;
    }>({
        id: "",
        isActive: false,
    });

    useEffect(() => {
        setFilteredList(allAgentList?.length > 0 ? allAgentList : []);

        setIsActive({
            id: "",
            isActive: false,
        });
    }, [allAgentList]);

    const { createConversationSuccess } = useAppSelector((state) => state.chat);
    const { fetchedSpaceUnreadCountSuccess } = useAppSelector((state) => state.space);

    const { fetchedInsightsSuccess, fetchingInsights, showPreviousChatLayout } = useAppSelector(
        (state) => state.flow
    );
    const { data: allConversations } = useGetAllAiConversations();

    const { messages, sendMessage, reset, isLoading, setMessages } = useCopilotChatHeadless_c();

    const handleModeChange = (mode: string) => {
        dispatch(flowActions.setFlowMode(mode));
    };

    const suggestions = [
        {
            title: "Give me a detailed breakdown of all sales orders placed this week, grouped by product.",
            icon: "ri-sparkling-fill",
        },
        {
            title: "Update the current inventory level for a specific product and let me know if it triggers a low-stock alert.",
            icon: "ri-sparkling-fill",
        },
        {
            title: "Tell me which products are at risk of going out of stock based on recent sales velocity and upcoming POs.",
            icon: "ri-sparkling-fill",
        },
    ];

    const handleUpload = (fileName, file, size) => {
        setUploadingDoc(true);
        firebaseService.uploadFile(
            `synthally/${process.env.REACT_APP_STAGE}/flow-docs/${fileName}`,
            file,
            (url) => {
                setFilesAttached((prevFiles) => [
                    ...prevFiles,
                    {
                        fileName: fileName,
                        path: url,
                        size: size,
                    },
                ]);
                setUploadingDoc(false);
                setLoadingAttachName("");
            }
        );
    };

    const deleteAttachment = (idx) => {
        const copyData = [...filesAttached];

        const imagePath = decodeURIComponent(
            copyData[idx]?.path?.split("/o/")?.[1]?.split("?alt")?.[0]
        );

        firebaseService.removeFile(imagePath, () => {
            copyData?.splice(idx, 1);
            setFilesAttached(copyData);
        });
    };

    useEffect(() => {
        setMode(flowMode ?? "operator");
    }, [flowMode]);

    const handleSendMessage = (content: string) => {
        if (isLoading) {
            return;
        }

        if (content.trim()) {
            setCreatingNewThread(true);
            setContentMessage(content);
            const body = {
                title: "New Chat",
                receiverType: "synth-gpt",
                conversationType: "sales_target_forecast",
            };
            dispatch(chatActions.createConversation(body));
        }
    };

    useEffect(() => {
        if (Boolean(createConversationSuccess)) {
            dispatch(chatActions.resetCreateConversationSuccess());
            const newThreadId = createConversationSuccess?.conversation?.threadId;
            dispatch(chatActions.fetchChatThread(newThreadId));
            navigate(`/dashboard/home/${newThreadId}`, {
                state: {
                    content: contentMessage,
                    filesAttached,
                    threadId: newThreadId,
                    selectedIds,
                },
            });
        }
    }, [createConversationSuccess]);

    useEffect(() => {
        dispatch(chatActions.fetchChatThread(uuidv4()));
    }, []);

    useEffect(() => {
        dispatch(flowActions.fetchInsights());
    }, []);

    useEffect(() => {
        if (fetchedInsightsSuccess) {
            setInsights(fetchedInsightsSuccess);
            dispatch(flowActions.resetInsights());
        }
    }, [fetchedInsightsSuccess]);

    useEffect(() => {
        setPreviousChatLayout(showPreviousChatLayout);
    }, [showPreviousChatLayout]);

    useEffect(() => {
        dispatch(spaceActions.fetchSpaceUnreadCount());
    }, []);

    useEffect(() => {
        if (Boolean(fetchedSpaceUnreadCountSuccess)) {
            setUnreadCount(fetchedSpaceUnreadCountSuccess?.total);
            dispatch(spaceActions.resetFetchSpaceUnreadCount());
        }
    }, [fetchedSpaceUnreadCountSuccess]);

    const hour = moment().hour();
    let period = "";
    if (hour < 12) {
        period = "Morning";
    } else if (hour < 17) {
        period = "Afternoon";
    } else {
        period = "Evening";
    }

    const displayActiveTab = () => {
        switch (activeTab) {
            case 0:
                return (
                    <FlowAllAgent
                        filteredList={filteredList}
                        setRefetch={setRefetch}
                        refetch={refetch}
                        setDebouncedSearch={setDebouncedSearch}
                        setType={setType}
                        type={type}
                        isActive={isActive}
                        setIsActive={setIsActive}
                    />
                );
            case 1:
                return <FlowAllTask filteredList={filteredList} profile={profile} />;
            default:
                return;
        }
    };

    return (
        <div className="font-rocGroteskMedium bg-white">
            <div className="mb-3 ">
                <PageTitleSection
                    className="!py-[10px]"
                    leftComp={
                        <BreadCrumb
                            parentClassName="!text-slate-900"
                            parentCrumb={"Home"}
                            parentUrl={"/dashboard/home"}
                            size="large"
                            parentIcon={
                                <i
                                    className={`-mt-0.5 text-slate-500 text-sm cursor-pointer transition-all duration-300 ${
                                        previousChatLayout ? "ri-side-bar-line" : "ri-side-bar-line"
                                    }`}
                                    onClick={() => {
                                        setShowWorkforce(false);
                                        dispatch(
                                            flowActions.showPreviousChatLayout(!previousChatLayout)
                                        );
                                    }}
                                ></i>
                            }
                        />
                    }
                    rightComp={
                        mode === "flow" && (
                            <div className="flex items-center space-x-[14px]">
                                <div>
                                    <div className="font-rocGroteskMedium relative">
                                        <a id={`spaces`}>
                                            <div className="relative">
                                                <div
                                                    className="bg-white border border-slate-200 rounded-[6px] py-2 px-4 text-[#142837] text-[13px] flex items-center gap-2 cursor-pointer"
                                                    onClick={() => {
                                                        navigate(SPACES);
                                                    }}
                                                >
                                                    <i className="ri-chat-1-line text-g-75"></i>
                                                    Spaces
                                                </div>
                                                {unreadCount > 0 && (
                                                    <p className="absolute -top-2 -left-2 bg-[#FFB902] py-[3px] px-[6px] rounded flex items-center justify-center text-[#042821] text-xs">
                                                        {unreadCount}
                                                    </p>
                                                )}
                                            </div>
                                        </a>

                                        <Tooltip
                                            anchorSelect={`#spaces`}
                                            place="bottom"
                                            content="Collaborative rooms for campaigns, launches, or projects. Chat, plan, and track work with your team in real time."
                                            style={{
                                                width: "260px",
                                                zIndex: 9999,
                                                backgroundColor: "#142837",
                                                color: "#fff",
                                                fontSize: "13px",
                                                fontWeight: "400 !important",
                                                borderRadius: "8px",
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="font-rocGroteskMedium">
                                    <a id={`workforceiq`}>
                                        <div
                                            className="bg-white border border-slate-200 rounded-[6px] py-2 px-4 text-[#142837] text-[13px] cursor-pointer flex items-center gap-2"
                                            onClick={() => {
                                                setShowWorkforce(true);
                                                setPreviousChatLayout(false);
                                            }}
                                        >
                                            <i className="ri-sparkling-fill text-g-75"></i>
                                            WorkforceIQ
                                        </div>
                                    </a>

                                    <Tooltip
                                        anchorSelect={`#workforceiq`}
                                        place="bottom"
                                        content="Your AI workforce hub. Create, automate and monitor AI agent tasks."
                                        style={{
                                            width: "270px",
                                            zIndex: 9999,
                                            backgroundColor: "#142837",
                                            color: "#fff",
                                            fontSize: "13px",
                                            fontWeight: "400 !important",
                                            borderRadius: "8px",
                                        }}
                                    />
                                </div>
                            </div>
                        )
                    }
                />
            </div>

            <div className={` ${previousChatLayout ? "flex gap-2" : ""} w-[100%]`}>
                <ChatHistory
                    previousChatLayout={previousChatLayout}
                    type={"flow"}
                    page={"flow"}
                    reset={reset}
                    threadId={threadId}
                    setThreadId={setThreadId}
                    contentMessage={contentMessage}
                    setContentMessage={setContentMessage}
                    setMessage={setMessage}
                    setMessages={setMessages}
                    messages={messages}
                    sendMessage={sendMessage}
                />

                <div
                    className={` gap-2 flex`}
                    style={{
                        width: `calc(100% - ${previousChatLayout ? "387px" : "0px"})`,
                    }}
                >
                    <div className="border border-[#F1F5F9] rounded-[10px] pb-[120px] flex-1 !overflow-y-scroll h-[calc(100vh-160px)]">
                        <div className="pt-[88px] w-[100%]  flex items-center justify-center flex-col">
                            <div className="w-full max-w-[642px] flex items-center justify-center flex-col mx-4 lg:mx-10">
                                <div className="flex bg-[#F8FAFC] rounded-lg p-1">
                                    {[
                                        {
                                            title: "Operator mode",
                                            mode: "operator",
                                            icon: "ri-tools-fill",
                                        },
                                        {
                                            title: "Flow mode",
                                            mode: "flow",
                                            img: "https://storage.googleapis.com/heysynth_files/synth_web/wave-line.svg",
                                        },
                                    ].map((item) => (
                                        <div className="font-rocGroteskMedium">
                                            <a id={`${item.mode}`}>
                                                <div
                                                    className={`flex items-center gap-1.5  py-0.5 px-3 ${
                                                        mode === item.mode && mode === "flow"
                                                            ? "bg-white border border-[#00C6FA] rounded-md"
                                                            : mode === item.mode &&
                                                              mode === "operator"
                                                            ? "bg-white border border-[#FD700B] rounded-md"
                                                            : "bg-[#F8FAFC]"
                                                    } cursor-pointer`}
                                                    onClick={() => {
                                                        setMode(item.mode);
                                                        handleModeChange(item.mode);
                                                    }}
                                                >
                                                    {item?.icon ? (
                                                        <i
                                                            className={`${item.icon} text-[#FD700B]`}
                                                        ></i>
                                                    ) : (
                                                        <img
                                                            src={item?.img}
                                                            alt="flow"
                                                            className="w-4 h-4"
                                                        />
                                                    )}
                                                    <p
                                                        className={`${
                                                            mode === item.mode
                                                                ? "text-g-75"
                                                                : "text-slate-500"
                                                        }  text-[13px]`}
                                                    >
                                                        {item.title}
                                                    </p>
                                                </div>
                                            </a>

                                            <Tooltip
                                                anchorSelect={`#${item.mode}`}
                                                place="top"
                                                content={
                                                    item?.mode === "flow"
                                                        ? "Simplified, focused workspace to collaborate, track, and decide together."
                                                        : "Detailed ops view for advanced controls."
                                                }
                                                style={{
                                                    width: "290px",
                                                    zIndex: 9999,
                                                    backgroundColor: "#142837",
                                                    color: "#fff",
                                                    fontSize: "13px",
                                                    fontWeight: "400 !important",
                                                    borderRadius: "8px",
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="pt-6 py-6">
                                    <h2 className="text-g-75 text-2xl font-rocGroteskMedium">
                                        {mode === "flow"
                                            ? `${period}, ${
                                                  profile?.user?.firstName ||
                                                  profile?.user?.lastName ||
                                                  profile?.user?.fullName ||
                                                  profile?.user?.profile?.businessName ||
                                                  ""
                                              }`
                                            : `Hi ${
                                                  profile?.user?.firstName ||
                                                  profile?.user?.lastName ||
                                                  profile?.user?.fullName ||
                                                  profile?.user?.profile?.businessName ||
                                                  ""
                                              }, how are you?`}
                                    </h2>
                                </div>

                                <div
                                    className={` ${
                                        mode === "flow"
                                            ? "gradient-border2 "
                                            : "border border-slate-200"
                                    } w-full rounded-lg px mt-2
                            shadow-[0px_4px_12px_-2px_rgba(212,212,212,0.03),0px_10px_15px_-4px_rgba(212,212,212,0.01),0px_8px_6px_0px_rgba(212,212,212,0.04)]`}
                                >
                                    {(uploadingDoc || filesAttached?.length > 0) && (
                                        <div
                                            className={`flex flex-nowrap overflow-x-auto items-center space-x-2 px-2.5 py-2 ${
                                                mode === "flow"
                                                    ? "bg-gradient-to-r from-[#F5FFEE] to-[#F5FFEE] rounded-xl"
                                                    : ""
                                            }`}
                                        >
                                            {loadingAttachName && uploadingDoc && (
                                                <div className="mb-2 items-center px-3 py-2  rounded-[6px] space-x-3 border border-slate-100 max-w-[250px] justify-between  opacity-50 inline-flex">
                                                    <div className="flex items-center gap-2">
                                                        <div className="bg-n-20 rounded-[6px] w-[25px] h-[25px] flex items-center justify-center">
                                                            <Loader size={4} />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-rocGroteskMedium text[#64748B]">
                                                                {truncate(loadingAttachName, {
                                                                    length: 25,
                                                                })}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {filesAttached?.length > 0 &&
                                                filesAttached?.map((doc, idx) => (
                                                    <div className="inline-flex">
                                                        <div className="flex space-x-1 w-full items-center">
                                                            <div
                                                                className="flex items-center px-2.5 py-[7px]  rounded-xl space-x-3 border border-slate-200 flex-shrink-0 w-[210px] justify-between bg-[#fff] 
                                        shadow-[0px_4px_12px_-2px_rgba(212,212,212,0.03),0px_10px_15px_-4px_rgba(212,212,212,0.01),0px_8px_6px_0px_rgba(212,212,212,0.04)]"
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    {displayFileIcon(
                                                                        doc?.fileName
                                                                            ?.split(".")
                                                                            .pop()
                                                                            ?.toLowerCase() || "",
                                                                        true,
                                                                        "w-[25px] h-[25px]"
                                                                    )}
                                                                    <div>
                                                                        <p
                                                                            className="text-xs font-rocGroteskMedium text-gm-50 cursor-pointer"
                                                                            onClick={() => {
                                                                                const a =
                                                                                    document.createElement(
                                                                                        "a"
                                                                                    );
                                                                                a.href = doc?.path;
                                                                                a.target = "_blank";
                                                                                document.body.appendChild(
                                                                                    a
                                                                                );
                                                                                a.click();
                                                                                document.body.removeChild(
                                                                                    a
                                                                                );
                                                                            }}
                                                                        >
                                                                            {truncate(
                                                                                doc?.fileName,
                                                                                {
                                                                                    length: 15,
                                                                                }
                                                                            )}
                                                                        </p>
                                                                        <p className="text-xs font-rocGroteskMedium text-slate-500">
                                                                            {convertBytes(
                                                                                Number(
                                                                                    doc?.size ?? 0
                                                                                )
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <i
                                                                    className="ri-close-fill text-gm-50 cursor-pointer"
                                                                    onClick={() =>
                                                                        deleteAttachment(idx)
                                                                    }
                                                                ></i>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}

                                            <div
                                                className="flex items-center  w-fit gap-4 cursor-pointer "
                                                onClick={() =>
                                                    document.getElementById("fileInput")?.click()
                                                }
                                            >
                                                <input
                                                    type="file"
                                                    id="fileInput"
                                                    className={`absolute z-[-1] opacity-0`}
                                                    // accept=".pdf"
                                                    multiple
                                                    onChange={(evt) => {
                                                        if (
                                                            !isFileSizeLessThanOrEqualTo(
                                                                evt.target.files[0],
                                                                25
                                                            )
                                                        ) {
                                                            toast.custom((t) => (
                                                                <CustomToast
                                                                    t={t}
                                                                    message={
                                                                        "File to large, file size cannot be more 25mb"
                                                                    }
                                                                    type={"error"}
                                                                />
                                                            ));
                                                            return;
                                                        } else {
                                                            setLoadingAttachName(
                                                                evt.target.files[0].name
                                                            );
                                                            handleUpload(
                                                                evt.target.files[0].name,
                                                                evt.target.files[0],
                                                                evt.target.files[0].size
                                                            );
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div
                                        className={`${
                                            mode === "flow"
                                                ? "bg-gradient-to-r from-[#F5FFEE] to-[#F5FFEE] rounded-xl"
                                                : ""
                                        } relative `}
                                        id="textAreaInput"
                                    >
                                        <CustomEditor
                                            message={message}
                                            setMessage={setMessage}
                                            setSelectedIds={setSelectedIds}
                                            selectedIds={selectedIds}
                                            currentSelectedIds={currentSelectedIds}
                                            setCurrentSelectedIds={setCurrentSelectedIds}
                                            sendMessage={handleSendMessage}
                                            hasPreviousMessage={messages?.length > 0}
                                            threadId={threadId}
                                            type={mode}
                                            page="flow"
                                            isLoading={isLoading || creatingNewThread}
                                            handleUpload={handleUpload}
                                            setLoadingAttachName={setLoadingAttachName}
                                            uploadingDoc={uploadingDoc}
                                        />
                                    </div>
                                </div>

                                <div
                                    className="py-[28px] flex items-stretch justify-center gap-[23px] w-full flex-wrap 
                            "
                                >
                                    {suggestions?.map((item, idx) => (
                                        <div
                                            className="border border-[#E2E8F0] p-[15px] rounded-[15px] bg-white hover:bg-slate-50 text-slate-500 hover:text-g-75 cursor-pointer shadow-[0px_4px_12px_-2px_rgba(212,212,212,0.03),0px_10px_15px_-4px_rgba(212,212,212,0.1),0px_8px_6px_0px_rgba(212,212,212,0.04)] w-[198px] inline-flex"
                                            onClick={() => {
                                                setMessage(item?.title);
                                            }}
                                        >
                                            <div className="">
                                                <i
                                                    className={`ri-sparkling-fill text-sm bg-gradient-to-r from-[#56B648] to-[#FFB902] bg-clip-text text-transparent`}
                                                ></i>
                                                <p className="text-sm ">
                                                    {truncate(item?.title, {
                                                        length: 65,
                                                    })}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="rounded-full border border-n-20 flex items-center justify-center w-8 h-8 shadow-[0px_4px_8px_-4px_rgba(16,24,40,0.03),0px_20px_24px_-4px_rgba(16,24,40,0.08)] cursor-pointer">
                                    <i className="ri-loop-right-line text-slate-500 text-sm"></i>
                                </div>
                            </div>

                            <div className="pt-10 flex items-center justify-start w-[100%] max-w-[1006px]  px-10">
                                <div className=" flex items-start justify-start flex-col w-full">
                                    {fetchingInsights ? (
                                        <div className="flex justify-start flex-col text-left mb-4">
                                            <p className="text-g-75 text-base mb-1">
                                                Daily SKU Insights
                                            </p>
                                            <p className="text-slate-500 text-sm">
                                                Here's what's happening across your inventory today
                                            </p>
                                            <div className="flex items-center justify-center w-full h-10">
                                                <Loader size={5} />
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex justify-start flex-col text-left mb-4">
                                                <p className="text-g-75 text-base mb-1">
                                                    Daily SKU Insights - {` `}
                                                    {moment(insights?.inventory?.createdAt).format(
                                                        "MMM D, h:mm A"
                                                    )}
                                                </p>
                                                <p className="text-slate-500 text-sm">
                                                    Here's what's happening across your inventory
                                                    today
                                                </p>
                                            </div>
                                            <div className="border border-slate-200 rounded-[12px] w-full">
                                                <div
                                                    onClick={() => {
                                                        setInsightsModal(true);
                                                        setSummary("inventory");
                                                    }}
                                                    className="hover:bg-slate-50 cursor-pointer rounded-t-xl flex py-4 px-5 gap-4 items-center border-b border-b-slate-200 text-[13px]"
                                                >
                                                    <div className="w-8 h-8 flex items-center justify-center rounded bg-[#E4281D]">
                                                        <i className="ri-box-2-fill text-white text-sm"></i>
                                                    </div>

                                                    <div className="">
                                                        <div className="flex items-center gap-1.5">
                                                            <p className="text-g-75">
                                                                Inventory Health
                                                            </p>
                                                            {insights?.inventory !== null && (
                                                                <p className="bg-[#E4281D] text-white px-1.5 rounded-[3px] text-[11px]">
                                                                    Stockout
                                                                </p>
                                                            )}
                                                        </div>
                                                        {insights?.inventory !== null ? (
                                                            <p className="text-slate-500">
                                                                {
                                                                    insights?.inventory?.stats
                                                                        ?.totalInventoryWithPotentialStockout
                                                                }{" "}
                                                                products across{" "}
                                                                {
                                                                    insights?.inventory?.stats
                                                                        ?.totalChannelsWithPotentialStockout
                                                                }{" "}
                                                                channels
                                                                {moment(
                                                                    insights?.inventory?.stats
                                                                        ?.earliestStockoutDate
                                                                )?.diff(
                                                                    moment().startOf("day"),
                                                                    "days"
                                                                ) < 1
                                                                    ? ` ${
                                                                          insights?.inventory?.stats
                                                                              ?.totalInventoryWithPotentialStockout >
                                                                          1
                                                                              ? "are"
                                                                              : "is"
                                                                      } projected to have gone out of stock.`
                                                                    : ` ${
                                                                          insights?.inventory?.stats
                                                                              ?.totalInventoryWithPotentialStockout >
                                                                          1
                                                                              ? "are"
                                                                              : "is"
                                                                      } projected to go out of stock within the next ${moment(
                                                                          insights?.inventory?.stats
                                                                              ?.earliestStockoutDate
                                                                      )?.diff(
                                                                          moment().startOf("day"),
                                                                          "days"
                                                                      )} days.`}
                                                            </p>
                                                        ) : (
                                                            <p className="text-slate-500">
                                                                No AI insights yet
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div
                                                    onClick={() => {
                                                        setInsightsModal(true);
                                                        setSummary("sales");
                                                    }}
                                                    className=" hover:bg-slate-50 cursor-pointer flex py-4 px-5 gap-4 items-center border-b border-b-slate-200 text-[13px]"
                                                >
                                                    <div className="w-8 h-8 flex items-center justify-center rounded bg-[#FA9600]">
                                                        <i className="ri-currency-line text-[#FFFBEB] text-sm"></i>
                                                    </div>

                                                    <div className="">
                                                        <p className="text-g-75">Sales MTD</p>
                                                        {insights?.sales !== null ? (
                                                            <p className="text-slate-500">
                                                                {
                                                                    insights?.sales?.stats
                                                                        ?.totalUnitsSold
                                                                }{" "}
                                                                units sold across all channels |
                                                                Avg. velocity:{" "}
                                                                {
                                                                    insights?.sales?.stats
                                                                        ?.averageSalesVelocityPerSku
                                                                }{" "}
                                                                units/sales per sku/channel/day
                                                            </p>
                                                        ) : (
                                                            <p className="text-slate-500">
                                                                No AI sales insights yet
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>{" "}
                                                <div
                                                    onClick={() => {
                                                        setInsightsModal(true);
                                                        setSummary("purchaseOrder");
                                                    }}
                                                    className=" hover:bg-slate-50 cursor-pointer rounded-b-xl flex py-4 px-5 gap-4 items-center text-[13px]"
                                                >
                                                    <div className="w-8 h-8 flex items-center justify-center rounded bg-slate-500">
                                                        <i className="ri-file-list-3-fill text-slate-100 text-sm"></i>
                                                    </div>
                                                    <div className="">
                                                        <div className="flex items-center gap-1.5">
                                                            <p className="text-g-75">Incoming PO</p>
                                                        </div>
                                                        {insights?.purchaseOrder !== null ? (
                                                            <p className="text-slate-500">
                                                                {insights?.purchaseOrder?.stats
                                                                    ?.totalIncomingPo > 0
                                                                    ? `${insights?.purchaseOrder?.stats?.totalIncomingPo} Purchase Orders
                                                                      Arriving This Month.`
                                                                    : "There are currently no scheduled purchase orders set to arrive within this month."}
                                                            </p>
                                                        ) : (
                                                            <p className="text-slate-500">
                                                                No AI PO insights yet
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>{" "}
                                            </div>{" "}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {showWorkforce && (
                        <div
                            className={`!overflow-y-scroll overflow-x-hidden h-[calc(100vh-212px)] border border-[#F1F5F9] rounded-[10px]  p-3 transition-all duration-300 ease-in-out ${
                                showWorkforce
                                    ? " opacity-100 w-[340px] visible"
                                    : "opacity-0 w-0 h-0 invisible"
                            } `}
                        >
                            <div className="flex gap-10">
                                <p className="text-g-75 text-base">
                                    Find, Create and Customize AI Agents for Back-Office Ops
                                </p>
                                <i
                                    className="ri-close-fill cursor-pointer"
                                    onClick={() => {
                                        setShowWorkforce(false);
                                        setPreviousChatLayout(showPreviousChatLayout);
                                    }}
                                ></i>
                            </div>

                            <div className="my-8 ">
                                <TabsContainer
                                    tabs={tabs}
                                    activeTab={activeTab}
                                    setActiveTab={(idx) => {
                                        setActiveTab(idx);
                                    }}
                                    component={displayActiveTab()}
                                    className="!px-0 overflow-x-auto relative"
                                    showButtonClassName
                                    itemClassName="!pb-2 max-lg:whitespace-nowrap !min-w-[70px]"
                                    borderLineClase={"!text-slate-100 w-full mb-6"}
                                    // tabContainerClassName=" "
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <FlowInsightModal
                insightsModal={insightsModal}
                setInsightsModal={setInsightsModal}
                summary={summary}
                insightsRecommendations={
                    summary === "inventory"
                        ? insights?.inventory?.recommendations
                        : summary === "sales"
                        ? insights?.sales?.recommendations
                        : insights?.purchaseOrder?.recommendations
                }
                insightsSummary={
                    summary === "inventory"
                        ? insights?.inventory?.summary
                        : summary === "sales"
                        ? insights?.sales?.summary
                        : insights?.purchaseOrder?.summary
                }
            />
        </div>
    );
};

export default Flow;
